import { combineEpics, ofType } from "redux-observable"
import { catchError, flatMap } from "rxjs/operators"
import { concat, EMPTY, from, Observable, of } from "rxjs"
import { Epic } from "../../epics"

import * as actions from "./actions"
import { increaseAttachmentsCount } from "../resourceheader/actions"
import { addErrorNotification } from "../notifications/actions"
import { RequestCancelledError } from "../../errors"
import { Labels } from "../../util/Translations"
import { ILinkedDocument } from "./index"

export const defaultUpdatingAttachmentDescriptionErrorMessage =
    "Something went wrong while trying to update the description."

export const requestAttachmentsEpic: Epic = (
    action$,
    _state$,
    { inlineAttachment: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_ATTACHMENTS),
        flatMap((action: actions.RequestAttachments) =>
            from(
                attachmentsAPI.getInlineAttachments(
                    action.itemId,
                    action.customFieldDefinitionId,
                ),
            ).pipe(
                flatMap((linkedDocuments: ILinkedDocument[]) =>
                    of(
                        actions.receiveAttachments(
                            linkedDocuments,
                            action.customFieldDefinitionId,
                        ),
                    ),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    return concat(
                        of(
                            addErrorNotification(
                                error.statusText || error.message || error,
                            ),
                        ),
                        of(
                            actions.receiveAttachments(
                                [],
                                action.customFieldDefinitionId,
                            ),
                        ),
                    )
                }),
            ),
        ),
    )

export const requestAddAttachmentEpic: Epic = (
    action$,
    _state$,
    { inlineAttachment: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_ADD_ATTACHMENT),
        flatMap((action: actions.RequestAddAttachment) =>
            from(
                attachmentsAPI.addInlineAttachment(
                    action.itemId,
                    action.customFieldDefinitionId,
                    action.formData,
                ),
            ).pipe(
                flatMap(() =>
                    concat(
                        of(
                            actions.registerFeedback(
                                action.itemId,
                                action.customFieldDefinitionId,
                            ),
                        ),
                        of(increaseAttachmentsCount(1)),
                        of(
                            actions.requestAttachments(
                                action.itemId,
                                action.customFieldDefinitionId,
                            ),
                        ),
                    ),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    const { AttachmentWasntUploaded } = Labels
                    if (error.text) {
                        return from(error.text()).pipe(
                            flatMap((errorMessage: string) =>
                                of(
                                    addErrorNotification(
                                        errorMessage || AttachmentWasntUploaded,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(addErrorNotification(AttachmentWasntUploaded))
                }),
            ),
        ),
    )

export const requestDeleteAttachmentEpic: Epic = (
    action$,
    state$,
    { inlineAttachment: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_DELETE_ATTACHMENT),
        flatMap(
            ({
                linkedDocumentId,
                customFieldDefinitionId,
                itemId,
            }: actions.RequestDeleteAttachment) => {
                const { attachmentFields, lightboxes } =
                    state$.value.inlineAttachments
                const { linkedDocuments } =
                    attachmentFields[customFieldDefinitionId]
                const lightboxCurrentIndex =
                    lightboxes[customFieldDefinitionId].currentIndex

                const shouldMoveToPreviousIndex =
                    lightboxCurrentIndex === linkedDocuments.length - 1
                const isLastAttachment = linkedDocuments.length === 1

                let lightboxFollowUpAction: Observable<
                    | actions.IncreaseLightboxCurrentIndex
                    | actions.UpdateLightboxVisibility
                    | never
                > = EMPTY
                if (isLastAttachment) {
                    lightboxFollowUpAction = of(
                        actions.updateLightboxVisibility(
                            customFieldDefinitionId,
                            false,
                        ),
                    )
                } else if (shouldMoveToPreviousIndex) {
                    lightboxFollowUpAction = of(
                        actions.increaseLightboxCurrentIndex(
                            customFieldDefinitionId,
                            -1,
                        ),
                    )
                }

                return from(
                    attachmentsAPI.removeAttachment(linkedDocumentId),
                ).pipe(
                    flatMap(() =>
                        concat(
                            of(
                                actions.registerFeedback(
                                    itemId,
                                    customFieldDefinitionId,
                                ),
                            ),
                            of(increaseAttachmentsCount(-1)),
                            lightboxFollowUpAction,
                            of(
                                actions.requestAttachments(
                                    itemId,
                                    customFieldDefinitionId,
                                ),
                            ),
                        ),
                    ),
                    catchError((error) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        return of(
                            addErrorNotification(
                                error.statusText || error.message || error,
                            ),
                        )
                    }),
                )
            },
        ),
    )

export const updateAttachmentDescriptionEpic: Epic = (
    action$,
    _state$,
    { inlineAttachment: { ipeAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_ATTACHMENT_DESCRIPTION),
        flatMap(
            ({
                objectId,
                value,
                itemId,
                customFieldDefinitionId,
            }: actions.UpdateAttachmentDescription) =>
                from(
                    ipeAPI.saveIPE(
                        {
                            object: "ItemDocument",
                            objectId,
                            propertyId: "LinkReason",
                        },
                        value,
                    ),
                ).pipe(
                    flatMap(() =>
                        of(
                            actions.requestAttachments(
                                itemId,
                                customFieldDefinitionId,
                            ),
                        ),
                    ),
                    catchError((error: unknown) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        console.error(error)
                        return of(
                            addErrorNotification(
                                defaultUpdatingAttachmentDescriptionErrorMessage,
                            ),
                        )
                    }),
                ),
        ),
    )

export default combineEpics(
    requestAttachmentsEpic,
    requestAddAttachmentEpic,
    requestDeleteAttachmentEpic,
    updateAttachmentDescriptionEpic,
)
